/**
 * Composants partagés pour les pages du site.
 * Petit design system interne pour respecter la charte et gagner en cohérence.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import BookingButton from "./BookingButton";

/** En-tête standard d'une page interne (titre + éventuel fil d'ariane). */
export function PageHero({ eyebrow, title, italic, subtitle, breadcrumb }) {
  return (
    <section className="pt-40 pb-16 px-6 lg:px-10" data-testid="page-hero">
      <div className="max-w-5xl mx-auto">
        {breadcrumb && (
          <nav aria-label="Fil d'Ariane" className="text-xs text-sand-500 mb-6" data-testid="breadcrumb">
            {breadcrumb.map((b, i) => (
              <span key={b.to || i}>
                {b.to ? (
                  <Link to={b.to} className="hover:text-sand-900">{b.label}</Link>
                ) : (
                  <span className="text-sand-700">{b.label}</span>
                )}
                {i < breadcrumb.length - 1 && <span className="mx-2 text-sand-400">›</span>}
              </span>
            ))}
          </nav>
        )}
        {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-light leading-[1.15] tracking-tight text-sand-900">
          {title}
          {italic && (
            <>
              <br />
              <em className="italic font-normal text-[#B07A45] inline-block mt-2">{italic}</em>
            </>
          )}
        </h1>
        {subtitle && (
          <p className="mt-6 text-lg text-sand-700 leading-relaxed max-w-2xl">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

/** Bloc de section avec typographie serif éditoriale. */
export function ProseSection({ children, className = "" }) {
  return (
    <section className={`px-6 lg:px-10 pb-16 ${className}`}>
      <div className="max-w-3xl mx-auto prose-mystic text-lg space-y-5 text-sand-700 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

/** Card cliquable vers une autre page (maillage interne + UX). */
export function InternalLinkCard({ to, eyebrow, title, description, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block rounded-2xl border border-sand-300/60 bg-white/60 p-7 transition-all duration-300 hover:border-sand-500/50 hover:shadow-lg hover:-translate-y-0.5"
    >
      {eyebrow && <p className="eyebrow !text-sand-500 mb-3">{eyebrow}</p>}
      <h3 className="font-serif text-2xl text-sand-900 leading-tight">{title}</h3>
      {description && <p className="mt-3 text-sand-700 leading-relaxed text-[15px]">{description}</p>}
      <span className="mt-5 inline-flex items-center gap-2 text-sm text-[#B07A45] group-hover:gap-3 transition-all">
        En savoir plus <ArrowRight size={14} />
      </span>
    </Link>
  );
}

/** CTA de fin de page réutilisable. */
export function EndCTA({ title = "Prêt·e à franchir le pas\u00A0?", text, testid = "cta-end" }) {
  return (
    <section className="py-24 bg-sand-200/40" data-testid={testid}>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="text-3xl sm:text-4xl font-serif font-light text-sand-900 leading-tight">{title}</h2>
        {text && <p className="mt-5 text-sand-700 max-w-2xl mx-auto leading-relaxed">{text}</p>}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <BookingButton testid={`${testid}-book`}>Réserver une séance</BookingButton>
          <Link to="/reserver" data-testid={`${testid}-contact`} className="btn-outline">
            Me contacter
          </Link>
        </div>
      </div>
    </section>
  );
}

/** Liste à puces personnalisée (point gold). */
export function BulletList({ items, testid }) {
  return (
    <ul className="space-y-3" data-testid={testid}>
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <Check size={18} className="shrink-0 mt-1 text-[#B07A45]" />
          <span className="text-sand-700 leading-relaxed">{it}</span>
        </li>
      ))}
    </ul>
  );
}
